import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import loader from "../../../../components/recharts/loader.gif";
import ToggleTab from "../Mod/ModTable/ChartTableToggle";
import ModHeader from "../Mod/ModTable/ModHeader";
import Modal from "../Mod/ModTable/Modal";
import { ClassesTable } from "./ClassesTable";
import ClassesChart from "./ClassesChart";
import {
  fetchModChartClassGrouped,
  postModChartClassGrouped,
  createClassBid,
} from "../../Utility/RestApi";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { useMeritEngineNavigation } from "../useMeritEngineNavigation";
import MainHeader from "../DateSession";
import Tooltip from "../../../../components/tooltip";
import { ReactComponent as Edit } from "../../../../icons/edit_solid.svg";

const DataTabs = ["chart", "table"];

export default function ClassesIndex() {
  const preSelectedDate = useSelector(
    (state: any) => state.modCSPDCLDate.selectedDate,
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modCSPDCLSession.modSession,
  );
  const { navigateToPage } = useMeritEngineNavigation();

  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(DataTabs[1]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalFilterOpen, setModalFilterOpen] = useState(false);

  const [selectedBlock, setSelectedBlock] = useState<number[]>([]);
  const handleSetSelectedBlock = (blocks: any[]) => {
    setSelectedBlock(
      blocks.map((b) =>
        typeof b === "object" ? Number(b.value ?? b.label) : Number(b),
      ),
    );
  };
  const [classesData, setClassesData] = useState<any[]>([]);

  const handleFetchData = useCallback(async () => {
    if (!preSelectedDate || !modSelectedSession) return;
    try {
      setLoading(true);
      const res = await fetchModChartClassGrouped(
        preSelectedDate,
        modSelectedSession,
      );
      setClassesData(res.data ?? []);
    } catch (err) {
      setError("Failed to fetch classes data");
      console.error("Error fetching classes data:", err);
    } finally {
      setLoading(false);
    }
  }, [preSelectedDate, modSelectedSession]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  const downloadTable = () => {
    const table = document.getElementById("classesTable");
    if (!table) {
      setError("Table is not visible right now!");
      return;
    }
    const worksheet = XLSX.utils.table_to_sheet(table);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "Classes_table.xlsx");
  };

  const handleFinalize = async () => {
    try {
      setSubmitLoading(true);
      const res = await createClassBid();
      if (res?.success === false) {
        throw new Error(res.message || "Failed to finalize classes");
      }
      navigateToPage("bid-creation");
    } catch (err) {
      setError("Failed to finalize classes");
      console.error("Error finalizing classes:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  async function handleUpdateReview(payload: any) {
    try {
      setSubmitLoading(true);
      const res = await postModChartClassGrouped(payload);
      if (res?.success === false) {
        throw new Error(res.message || "Failed to update classes data");
      }
      await handleFetchData();
      setModalOpen(false);
    } catch (err) {
      setError("Failed to update classes data");
      console.error("Error updating classes:", err);
    } finally {
      setSubmitLoading(false);
    }
  }

  return (
    <div className="page-wrapper">
      <div className="flex flex-col w-full">
        <MainHeader />
        <div className="tabContent">
          <div className="h-[calc(-73px+100vh)] p-4">
            <div className="w-full h-full flex flex-col gap-4">
              <ModHeader title="Classes" />
              <div className="gas-chart-container flex-1 flex flex-col min-h-0 !gap-1">
                <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
                  <div className="flex items-center gap-3">
                    <ToggleTab
                      tabs={DataTabs}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                    />
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Slot"
                      selectedOptions={selectedBlock}
                      setSelectedOptions={handleSetSelectedBlock as any}
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setModalOpen(true)}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium"
                    >
                      <Edit className="w-5 h-5" />
                      Modify
                    </button>
                    <button
                      onClick={downloadTable}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C]"
                    >
                      <span>Download Data</span>
                      <Download className="w-5 h-5" />
                    </button>
                    <button
                      disabled={submitLoading}
                      onClick={handleFinalize}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                    >
                      {submitLoading
                        ? "Processing..."
                        : "Finalize & Create Bid"}
                    </button>
                  </div>
                </div>

                <div className="chartTable flex-1 min-h-0 overflow-hidden">
                  {loading ? (
                    <div className="h-full w-full bg-white flex items-center justify-center">
                      <img
                        src={loader}
                        width={200}
                        alt="loader"
                        className="block mt-[5rem]"
                      />
                    </div>
                  ) : (
                    <>
                      {activeTab === "table" && (
                        <div className="mod-table-load-in h-full min-h-0 flex flex-col">
                          <ClassesTable
                            setModalOpen={setModalOpen}
                            selectedBlock={selectedBlock}
                            apiData={classesData}
                            rawApiData={classesData}
                            maxHeight="calc(100vh - 260px)"
                          />
                        </div>
                      )}
                      {activeTab === "chart" && (
                        <ClassesChart
                          selectedBlock={selectedBlock}
                          data={classesData}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>

              <ErrorPopup error={error} onClose={() => setError(null)} />

              <div className="gna-footer-container">
                <div className="grid grid-cols-3 justify-between gap-5">
                  <Tooltip
                    note={
                      <p>
                        Classes data showing Quantum, Rate, BM, SM, SAM per
                        class.
                      </p>
                    }
                    text={` 1. Quantum: Power Quantum (MW).
                            2. Rate: Variable Charge Rate (Rs/kWh).
                            3. BM: Buy Margin (p/u).
                            4. SM: Sell Margin (p/u).
                            5. SAM: Sell Aggressive Margin (p/u).`}
                  >
                    <p className="gna-footer-text">Source</p>
                  </Tooltip>
                  <p className="gna-footer-text col-span-1 text-center">
                    Powered by <strong>GNA Energy</strong>
                  </p>
                  <strong className="gna-footer-text col-span-1 text-end">
                    Beta Version
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
          <div
            className="w-full flex flex-col"
            style={{ maxHeight: "calc(100vh - 60px)" }}
          >
            {/* Header row */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#DFEAF2]">
              <div className="flex items-center gap-3">
                <h3 className="text-[#525062] text font-semibold">
                  Classes Table
                </h3>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#00B36B] bg-[#F5FFF8] px-3 py-1.5 text-[10px] font-medium leading-none text-[#00B36B] shadow-[0_2px_10px_rgba(0,179,107,0.12)]">
                  <span
                    className="h-2 w-2 rounded-full bg-[#00B36B]"
                    aria-hidden="true"
                  />
                  Modification Mode
                </span>
              </div>
              <button
                onClick={() => setModalFilterOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium hover:bg-[#ddf4f9] transition-colors"
              >
                <Edit className="w-4 h-4" />
                Batch Customize
              </button>
            </div>

            {/* Table */}
            <div className="mod-table-load-in flex-1 min-h-0 p-5 pb-2 flex flex-col">
              <ClassesTable
                setModalOpen={setModalOpen}
                mode="edit"
                handleSubmit={handleUpdateReview}
                loading={submitLoading}
                selectedBlock={selectedBlock}
                apiData={classesData}
                rawApiData={classesData}
                modalFilter={isModalFilterOpen}
                setModalFilter={setModalFilterOpen}
                maxHeight="calc(100vh - 260px)"
              />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
